/**
 * Ближайший прокручиваемый родитель. На страницах CRM прокручивается внутренний
 * контейнер (`#apelsin-app-content`), а окно — нет: у обёртки `overflow: hidden`.
 */
const findScrollableParent = (element: HTMLElement): HTMLElement | null => {
    let parent = element.parentElement;

    while (parent) {
        const { overflowY } = getComputedStyle(parent);

        if (/auto|scroll|overlay/.test(overflowY) && parent.scrollHeight > parent.clientHeight) {
            return parent;
        }

        parent = parent.parentElement;
    }

    return null;
};

/**
 * Прокручивает к элементу, оставляя над ним `offset` пикселей.
 *
 * Отступ считаем в системе координат того, что реально прокручивается. Раньше
 * здесь сначала звали `scrollIntoView`, а затем через `setTimeout` — ещё и
 * `window.scrollTo`; на страницах CRM окно не прокручивается вовсе, поэтому
 * вторая половина была пустышкой: `offset` не влиял ни на что, и элемент
 * прилипал к самому верху контейнера. Заодно тот `setTimeout` обращался к
 * `element` без проверки на null и падал, если элемента на странице не было.
 */
export const scrollToNode = (element: HTMLElement | null, offset = 20) => {
    if (!element) return;

    const scroller = findScrollableParent(element);

    if (!scroller) {
        window.scrollTo({
            top: element.getBoundingClientRect().top + window.pageYOffset - offset,
            behavior: 'smooth',
        });

        return;
    }

    const elementOffsetInScroller =
        element.getBoundingClientRect().top - scroller.getBoundingClientRect().top;

    scroller.scrollTo({
        top: scroller.scrollTop + elementOffsetInScroller - offset,
        behavior: 'smooth',
    });
};

/** То же самое для элемента, у которого есть id. */
export const scrollToElement = (elementId: string, offset = 20) =>
    scrollToNode(document.getElementById(elementId), offset);

/**
 * Текущее положение прокрутки области, в которой лежит элемент.
 *
 * Нужно, когда возвращаться надо не к самому элементу, а туда, где человек
 * стоял до перехода: позиция элемента к тому моменту уже другая — над ним
 * успели появиться или исчезнуть строки.
 */
export const getScrollOffset = (element: HTMLElement | null): number => {
    if (!element) return 0;

    return findScrollableParent(element)?.scrollTop ?? window.scrollY;
};

/**
 * Сколько ждём появления клавиатуры после фокуса. Не дождались — значит её нет
 * (десктоп или внешняя клавиатура), и повторять прокрутку незачем.
 */
const KEYBOARD_WAIT_MS = 1000;

/**
 * Через сколько повторить прокрутку вслепую. Android не всегда сообщает об
 * изменении видимой области, поэтому одного события `resize` мало.
 */
const KEYBOARD_RETRY_MS = 350;

/**
 * Прокручивает к элементу под фокус ввода и повторяет это, когда поднимется
 * экранная клавиатура.
 *
 * Здесь `scrollIntoView`, а не собственный расчёт: прокрутку ведёт браузер по
 * всем прокручиваемым предкам сразу, и неважно, который из них на этот раз
 * настоящий. На Android контейнер оказывается другим, чем на десктопе, и
 * расчёт «вручную» попадал в элемент, который не прокручивается, — страница не
 * двигалась вовсе.
 *
 * Отступ сверху задаётся элементу стилем `scroll-margin-top`: `scrollIntoView`
 * его учитывает, отдельного параметра не нужно.
 *
 * `behavior: 'auto'` намеренно: плавную прокрутку на телефоне перебивает
 * собственный подскок браузера к полю, и страница остаётся на месте.
 */
export const scrollToElementWithKeyboard = (elementId: string) => {
    const scrollToTarget = () => {
        document.getElementById(elementId)?.scrollIntoView({
            block: 'start',
            behavior: 'auto',
        });
    };

    scrollToTarget();

    window.setTimeout(scrollToTarget, KEYBOARD_RETRY_MS);

    const viewport = window.visualViewport;

    if (!viewport) return;

    const handleViewportResize = () => {
        scrollToTarget();
        stopWaiting();
    };

    const stopWaiting = () => {
        viewport.removeEventListener('resize', handleViewportResize);
        window.clearTimeout(deadline);
    };

    const deadline = window.setTimeout(stopWaiting, KEYBOARD_WAIT_MS);

    viewport.addEventListener('resize', handleViewportResize);
};

/** Возвращает прокрутку в положение, снятое через `getScrollOffset`. */
export const scrollToOffset = (element: HTMLElement | null, top: number) => {
    const scroller = element && findScrollableParent(element);

    if (!scroller) {
        window.scrollTo({ top, behavior: 'smooth' });
        return;
    }

    scroller.scrollTo({ top, behavior: 'smooth' });
};
